import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle, Info, X, Trash2 } from "lucide-react";

// ─── Global singleton ──────────────────────────────────────────────────────
let _open = null;

export const dialog = {
  confirm(message, options = {}) {
    return new Promise((resolve) => {
      _open?.({ type: "confirm", message, resolve, ...options });
    });
  },
  alert(message, options = {}) {
    return new Promise((resolve) => {
      _open?.({ type: "alert", message, resolve, ...options });
    });
  },
  prompt(message, defaultValue = "", options = {}) {
    return new Promise((resolve) => {
      _open?.({ type: "prompt", message, defaultValue, resolve, ...options });
    });
  },
};

// ─── Variants ──────────────────────────────────────────────────────────────
const VARIANTS = {
  danger: {
    icon: Trash2,
    iconBg: "bg-red-50",
    iconColor: "text-red-600",
    btnClass: "bg-red-600 hover:bg-red-700 text-white",
  },
  warning: {
    icon: AlertTriangle,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    btnClass: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  success: {
    icon: CheckCircle,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    btnClass: "bg-emerald-600 hover:bg-emerald-700 text-white",
  },
  default: {
    icon: Info,
    iconBg: "bg-[#840040]/10",
    iconColor: "text-[#840040]",
    btnClass: "bg-[#840040] hover:bg-[#6c0034] text-white",
  },
};

// ─── Provider ──────────────────────────────────────────────────────────────
export function DialogProvider() {
  const [state, setState] = useState(null);
  const [promptValue, setPromptValue] = useState("");

  useEffect(() => {
    _open = (s) => {
      if (s?.type === "prompt") setPromptValue(s.defaultValue ?? "");
      setState(s);
    };
    return () => { _open = null; };
  }, []);

  const close = useCallback((result) => {
    state?.resolve(result);
    setState(null);
  }, [state]);

  const closePrompt = useCallback((confirmed) => {
    state?.resolve(confirmed ? promptValue : null);
    setState(null);
  }, [state, promptValue]);

  useEffect(() => {
    const onKey = (e) => {
      if (!state) return;
      if (e.key === "Escape") close(false);
      if (e.key === "Enter" && state.type !== "prompt") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  if (!state) return null;

  const isConfirm = state.type === "confirm";
  const isPrompt = state.type === "prompt";
  const v = VARIANTS[state.variant ?? (isConfirm ? "danger" : "default")];
  const Icon = v.icon;

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={() => isPrompt ? closePrompt(false) : close(false)}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-[400px] mx-4 p-6 space-y-4"
        style={{ animation: "dialogIn 0.15s ease-out" }}
      >
        {/* Close */}
        <button
          onClick={() => isPrompt ? closePrompt(false) : close(false)}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Icon */}
        <div className="flex justify-center pt-1">
          <div className={`p-3 rounded-full ${v.iconBg}`}>
            <Icon className={`w-6 h-6 ${v.iconColor}`} />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-center text-base font-semibold text-slate-800">
          {state.title ?? (isConfirm ? "Confirmation" : "Information")}
        </h3>

        {/* Message */}
        <p className="text-center text-sm text-slate-600 leading-relaxed">
          {state.message}
        </p>

        {/* Input (prompt only) */}
        {isPrompt && (
          <input
            autoFocus
            type="text"
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") closePrompt(true); }}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#840040]/30 focus:border-[#840040]"
          />
        )}

        {/* Buttons */}
        <div className={`flex gap-2 pt-1 ${isConfirm || isPrompt ? "" : "justify-center"}`}>
          {(isConfirm || isPrompt) && (
            <button
              onClick={() => isPrompt ? closePrompt(false) : close(false)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors font-medium"
            >
              {state.cancelLabel ?? "Annuler"}
            </button>
          )}
          <button
            onClick={() => isPrompt ? closePrompt(true) : close(true)}
            className={`${isConfirm || isPrompt ? "flex-1" : "px-8"} py-2.5 rounded-xl text-sm font-semibold transition-colors ${v.btnClass}`}
          >
            {state.confirmLabel ?? (isConfirm || isPrompt ? "Confirmer" : "OK")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
